/* eslint-disable no-console */
import {
  AuthServiceUrls,
  AuthUtils,
  CloudActions,
  CloudErrors,
  CloudUtils,
  CostCenterServiceUrls,
  FlowjoCommons,
  LicenseServiceUrls,
  ProductServiceUrls,
  User,
  UserAdminServiceUrls,
  UserServiceUrls,
} from '@portal/portal-core';
import { EDITABLE_INPUT_FIELD_ACTIVE } from 'constants/ui';
import lockr from 'utils/local-store';
import { defaultCostCenterObject } from 'utils';

const { Alerts, Net } = FlowjoCommons;

// Mirror the Promise.race timeout strategy used in the super-admin API layer.
// All sync-polling callers handle `undefined` responses already (connection alerts).
const SYNC_TIMEOUT_MS = 15000;
const withSyncTimeout = <T>(promise: Promise<T>): Promise<T | undefined> =>
  Promise.race([
    promise,
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), SYNC_TIMEOUT_MS)),
  ]);

/*
 * Boundary types. This module wires together the (untyped) auth-client `config` object, the
 * Redux `store`, arbitrary Redux `action`s, and network responses from cloud-core's `Net`
 * helpers. None of those are typed in this package, so rather than reconstruct large domain
 * interfaces we model them structurally at the boundary.
 */

/**
 * The auth-client configuration object injected at runtime. It exposes many methods/props
 * (`getAuthJwtToken`, `serverUrl`, `navigateTo`, `isSuperAdmin`, ...) that live in the
 * consuming app; modeled as an untyped boundary record.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped auth-client config boundary
type Config = Record<string, any>;

/** Redux store boundary: `getState()` returns the untyped runtime state tree. */
interface Store {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped runtime state tree
  getState: () => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- forwards arbitrary Redux actions
  dispatch: (action: any) => any;
}

/** A Redux action; only `type` is known, the remaining payload varies per action. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- arbitrary Redux action payload
type ReduxAction = { type?: string; [key: string]: any };

/** The `next` handler in the Redux middleware chain. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Redux middleware next boundary
type Next = (action: any) => any;

/**
 * Response envelope resolved by cloud-core's `Net.sendJson`/`Net.getJson`. Those resolve as
 * `unknown` and the payload shape varies per endpoint (`data` is a JSON string for some
 * endpoints and an already-parsed object for others), so this is a loose envelope.
 */
interface NetResponse {
  code: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload shape varies per endpoint
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- endpoints add arbitrary fields
  [key: string]: any;
}

/** Normalized result of a user/admin sync fetch. */
interface SyncResponse {
  isAuthenticated: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped sync payload from server
  sync: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- user id derived from JWT (untyped)
  userId: any;
}

// Requests a client config object from the service. Used by auth-client on initial page load to get
// clientside config values
export function getClientConfigAsync(serverUrl: string): Promise<unknown> {
  return Net.getJson(AuthServiceUrls.clientConfigUrl(serverUrl));
}

export function forceUpdateCurrentCostCenter(store: Store): void {
  const userAdminState = store.getState().userAdmin;
  const currentCostCenter = userAdminState.costCenter;
  if (currentCostCenter) {
    // cloud-core's `userAdminForceUpdate(costCenterId, productName, requestId?)` requires the
    // `productName` arg; the original call omitted it, so pass `undefined` to preserve the exact
    // runtime action shape (productName undefined) while satisfying the typed arity.
    const forceUpdateAction = CloudActions.userAdminForceUpdate(currentCostCenter.id, undefined);
    store.dispatch(forceUpdateAction);
  }
}

export function missingUserAgreement(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_FIND_USER_AGREEMENT;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissSignInConnectionProblem(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export const dismissUserAdminSyncAlert = (store: Store): void =>
  dismissSignInConnectionProblem(store);

export function dismissmissingUserAgreement(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_FIND_USER_AGREEMENT;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function dismissUnauthorizedUserAdminAlert(store: Store): void {
  // `CANNOT_AUTHORIZE_USER_ADMIN` is not a member of `cloudErrorTypes` (it lives on
  // `userAdminErrorTypes`), so at runtime this access resolves to `undefined`. Cast through an
  // index signature to preserve that exact (pre-existing) runtime value rather than silently
  // changing which alert id is dismissed.
  const errorType = (CloudErrors.cloudErrorTypes as Record<string, string>)
    .CANNOT_AUTHORIZE_USER_ADMIN;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function userAdminSyncAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function authUserAdminFetchSyncInfoAsync(
  config: Config,
  store: Store,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw JWT string boundary
  authToken: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped inventory payload
  inventory: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- optional boolean-ish flag
  initializeAdminSync?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- optional cost center id
  forceUpdate?: any,
): Promise<SyncResponse | undefined> {
  const userId = AuthUtils.getUserIdFromJWT(authToken);
  let adminId = userId;

  // If we're in a superadmin build *and* a specific admin ID is requested in the URL query params,
  // treat it as admin ID:
  // NOTE: config.isSuperAdmin is optional — it is only provided in super-admin builds.
  // Always guard with `config.isSuperAdmin && config.isSuperAdmin()` before calling.
  if (config.isSuperAdmin && config.isSuperAdmin()) {
    const specificRequestID = (CloudUtils.getCurrentQueryParams() as { adminID?: string }).adminID;
    if (specificRequestID) {
      adminId = specificRequestID;
    }
  }

  const headers = { Authorization: `Bearer ${authToken}` };
  const url = UserAdminServiceUrls.userAdminSyncURL(config.serverUrl, adminId);
  let syncAction;
  if (initializeAdminSync) {
    syncAction = CloudActions.syncUserAdminInitialize(adminId);
  } else if (forceUpdate) {
    syncAction = CloudActions.syncUserAdmin(adminId, { id: forceUpdate, version: 0 }, {});
    store.dispatch(syncAction);
  } else {
    // mica
    syncAction = CloudActions.syncUserAdmin(adminId, inventory.costCenter, inventory.users);
  }

  return withSyncTimeout((Net.sendJson(url, syncAction, headers) as Promise<NetResponse>).then((response) => {
    if (response.code >= 500) {
      userAdminSyncAlert(store);
    } else if (response.data) {
      dismissUserAdminSyncAlert(store);
      dismissUnauthorizedUserAdminAlert(store);

      response = JSON.parse(response.data);
      if (response.succeeded && response.result) {
        let syncResult = response.result;

        // In case of initial Super Admin sync, we may need to slim down the payload. If admin has
        // MULTIPLE cost centers, we only want the one requested in params... unless admin and
        // superadmin are one and the same user, in which case we include all cost centers to
        // provide normal admin experience.
        //
        // TODO[FJCLOUD-1428]: consider putting server in charge of this logic.
        if (initializeAdminSync) {
          if (config.isSuperAdmin && config.isSuperAdmin()) {
            const costCenterID = (
              CloudUtils.getCurrentQueryParams() as { costCenterId?: string }
            ).costCenterId;
            syncResult = Object.keys(response.result)
              .filter((key) => {
                if (key === costCenterID) {
                  return true;
                }
                const costCenter = response.result[key];
                return AuthUtils.isUserAdmin(userId, costCenter);
              })
              .reduce(
                (total, key) =>
                  Object.assign(total, {
                    [key]: response.result[key],
                  }),
                {},
              );
          }
        }

        const result: SyncResponse = {
          isAuthenticated: true,
          sync: syncResult,
          userId,
        };
        return result;
      }
    }
    return undefined;
  }));
}

export function authBuildUserAdminInventory(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped admin cost center list
  adminCostCenterList: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped cost center object
  costCenter: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped users map
  users: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose inventory record
): { costCenter: Record<string, any>; users: Record<string, any> } | null {
  if (!adminCostCenterList || !costCenter) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose inventory record
  const inventory: { costCenter: Record<string, any>; users: Record<string, any> } = {
    costCenter: {},
    users: {},
  };
  if (adminCostCenterList && costCenter) {
    inventory.costCenter = {
      id: costCenter.id,
      version: costCenter.version || 0,
    };
  }
  if (users) {
    for (const userId in users) {
      const user = users[userId];
      const newUsers = { ...inventory.users, [userId]: user.version };
      inventory.users = newUsers;
    }
  }
  return inventory;
}

export function authUserAdminCheckForUpdates(
  config: Config,
  store: Store,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- boolean-ish flag boundary
  initializeAdminSync?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- optional cost center id
  forceUpdate?: any,
): void {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const editing = lockr.get(EDITABLE_INPUT_FIELD_ACTIVE);
  if (!authToken || editing) return;

  const inventory = {
    billingUnits: {},
    users: {},
  };

  if (initializeAdminSync) {
    authUserAdminFetchSyncInfoAsync(config, store, authToken, inventory, initializeAdminSync).then(
      (response) => {
        if (!response) {
          return;
        }
        if (response && response.userId) {
          config.hideAuth(response.userId);
          dismissUserAdminSyncAlert(store);
        } else {
          userAdminSyncAlert(store);
        }

        if (response && response.sync) {
          const adminCostCenterList = response.sync;
          const adminCostCentersAction = CloudActions.syncAdminCostCenterList(
            response.userId,
            adminCostCenterList,
          );
          store.dispatch(adminCostCentersAction);
        }
      },
    );
  } else {
    const { adminCostCenterList } = store.getState().userAdmin;
    const costCenter = store.getState().userAdmin.costCenter || {};
    const users = store.getState().userAdmin.users || {};

    if (!adminCostCenterList) {
      const initializeAdminSync = true;

      forceUpdate = undefined;
      authUserAdminFetchSyncInfoAsync(
        config,
        store,
        authToken,

        inventory,
        initializeAdminSync,
        forceUpdate,
      ).then((response) => {
        if (!response) {
          return;
        }
        if (response && response.userId) {
          config.hideAuth(response.userId);
          dismissUserAdminSyncAlert(store);
        } else {
          userAdminSyncAlert(store);
        }

        if (response && response.sync) {
          const adminCostCenterList = response.sync;
          const adminId = response.userId;
          const adminCostCentersAction = CloudActions.syncAdminCostCenterList(
            adminId,
            adminCostCenterList,
          );
          store.dispatch(adminCostCentersAction);
        }
      });
      return;
    }

    // Renamed from `inventory` to break shadowing of the outer function-scoped `inventory`
    // (declared at the top of this function). As a block-scoped `let inventory`, this declaration
    // placed the `inventory` name in the else-block's temporal dead zone, so the `inventory`
    // reference in the `!adminCostCenterList` branch above resolved to this (still-unassigned)
    // binding — which TypeScript flags as a use-before-declaration. Renaming lets that earlier
    // reference bind to the intended outer placeholder inventory.
    const builtInventory = authBuildUserAdminInventory(adminCostCenterList, costCenter, users);

    authUserAdminFetchSyncInfoAsync(
      config,
      store,
      authToken,
      builtInventory,
      initializeAdminSync,
      forceUpdate,
    ).then((response) => {
      if (!response) {
        return;
      }
      if (response.userId) {
        config.hideAuth(response.userId);
        dismissUserAdminSyncAlert(store);
      } else {
        userAdminSyncAlert(store);
      }
      if (response.sync) {
        const invites = response.sync.costCenterInvites;

        const { costCenter, users, userAuthorizedDevices, licenseUsage } = response.sync;
        const adminInvitesAction = config.syncAdminInvitesAction(invites);
        store.dispatch(adminInvitesAction);
        const usersAction = CloudActions.syncUserAdminUsers(response.userId, users);
        store.dispatch(usersAction);
        const costCenterAction = CloudActions.syncUserAdminCostCenter(response.userId, costCenter);
        store.dispatch(costCenterAction);
        const authorizedDevicesAction = CloudActions.syncUserAdminAuthorizedDevices(
          response.userId,
          userAuthorizedDevices,
        );
        store.dispatch(authorizedDevicesAction);
        const lastUsedAction = CloudActions.syncUserAdminLastUsed(response.userId, licenseUsage);
        store.dispatch(lastUsedAction);
      }
    });
  }
}

export function authBuildInventory({
  costCenters,
  users,
  authorizedDevices,
  authorizedDeviceRemovalsRemaining,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped cost centers map
  costCenters: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped users map
  users: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped authorized devices map
  authorizedDevices: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped device removals map
  authorizedDeviceRemovalsRemaining: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose inventory record
}): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose inventory record
  const inventory: Record<string, any> = {};
  // Comparing against fresh object literals via `===` is always false (reference comparison). This
  // is preserved from the original code; the `as unknown` casts keep that exact runtime behavior
  // while silencing TS's "unintentional comparison" diagnostic.
  if (costCenters === ({} as unknown) || users === ({} as unknown)) {
    for (const costCenterId in costCenters) {
      const costCenter = costCenters[costCenterId];
      inventory[costCenterId] = costCenter.version;
    }

    for (const userId in users) {
      const user = users[userId];
      inventory[userId] = user.version;
    }

    for (const authorizedDeviceId in authorizedDevices) {
      const authorizedDevice = authorizedDevices[authorizedDeviceId];
      inventory[authorizedDeviceId] = authorizedDevice.version;
    }

    for (const authorizedDeviceRemovalId in authorizedDeviceRemovalsRemaining) {
      const authorizedDeviceRemoval = authorizedDeviceRemovalsRemaining[authorizedDeviceRemovalId];
      inventory[authorizedDeviceRemovalId] = authorizedDeviceRemoval.version;
    }

    return { inventory };
  }

  return {
    authorizedDeviceRemovalsRemaining: {},
    authorizedDevices: {},
    billingUnits: {},
    users: {},
  };
}

export function userSyncAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissUserSyncAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

function authFetchSyncInfoAsync(
  config: Config,
  store: Store,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw JWT string boundary
  authToken: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped inventory payload
  inventory: any,
): Promise<SyncResponse | undefined> {
  const userId = AuthUtils.getUserIdFromJWT(authToken);
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = UserServiceUrls.userSyncURL(config.serverUrl, userId);

  return withSyncTimeout((Net.sendJson(url, inventory, headers) as Promise<NetResponse>).then((res) => {
    if (res.code >= 500) {
      userSyncAlert(store);
    } else if (res.data) {
      dismissUserSyncAlert(store);

      const response = JSON.parse(res.data);
      if (response.succeeded && response.result) {
        const syncResult = response.result;
        const timestamp = response.result.timestampUtc;
        if (timestamp) {
          config.setClientStorageValue('apiTimestamp', timestamp);
          config.setClientStorageValue('clientTimestamp', CloudUtils.getCurrentEpochTimeUtc());
        }
        const result: SyncResponse = {
          isAuthenticated: true,
          sync: syncResult,
          userId,
        };
        return result;
      }
      if (
        !response.succeeded &&
        response.error === 'Unauthorized' &&
        response.message === 'jwt expired'
      ) {
        store.dispatch(config.signOut());
      } else if (response.error === 'Unauthorized') {
        config.launchAuth('Unauthorized Access');
      } else {
        config.launchAuth('Unknown Service Error');
      }
    }
    return undefined;
  }));
}

export function authCheckForUpdates(config: Config, store: Store): void {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const state = store.getState();
  const inventory = authBuildInventory({
    costCenters: state.costCenters,
    users: state.users,
    authorizedDevices: state.authorizedDevices,
    authorizedDeviceRemovalsRemaining: state.authorizedDeviceRemovalsRemaining,
  });

  authFetchSyncInfoAsync(config, store, authToken, inventory).then((response) => {
    if (response && response.userId) {
      config.hideAuth(response.userId);
      dismissUserSyncAlert(store);
    } else {
      userSyncAlert(store);
    }
    if (response && response.sync) {
      const {
        users,
        authorizedDevices,
        authorizedDeviceRemovalsRemaining,
        billingUnits,
        pendingUserProductInvites,
        products,
      } = response.sync;

      store.dispatch(config.syncUserInvitesAction(pendingUserProductInvites));
      store.dispatch(CloudActions.users(users));
      store.dispatch(CloudActions.costCenters(billingUnits));
      store.dispatch(CloudActions.authorizedDevices(authorizedDevices));
      store.dispatch(
        CloudActions.authorizedDeviceRemovalsRemaining(authorizedDeviceRemovalsRemaining),
      );
      // Verifier-aware filtering (see ProductManager.getProductsForUserAsync,
      // shared with GET /api/user/v1/products) happens server-side before this
      // ever reaches the client, so products can go straight to the reducer on
      // every poll cycle without a second round trip.
      if (products) {
        store.dispatch(CloudActions.products(products));
      }
    }
  });
}
// @TODO refactor cloudWatcher into it's own Sagas and Utils in the user-client
export function cloudWatcher(config: Config, store: Store): void {
  const jwt = config.getAuthJwtToken(config.getAuthTokenKey());
  // if (jwt) {
  //   userAgreementUpdates(config, store);
  // }
  (function cycleAuthCheckForUpdates(): void {
    const jwtStillHere = config.getAuthJwtToken(config.getAuthTokenKey());
    if (jwtStillHere) {
      const expTimestampFromToken = AuthUtils.getClaimFromJWT(jwt, 'exp');
      const clientTimestampCompensated =
        -1 * config.getClientToApiTimestampDiff() + Math.floor(CloudUtils.getCurrentEpochTimeUtc());
      const tokenExpDiff = expTimestampFromToken - clientTimestampCompensated * 0.001;
      if (tokenExpDiff < 120) {
        return store.dispatch(config.signOut());
      }
      if (navigator.onLine && !document.hidden) {
        const userId = config.getCurrentUserIdFromJwt(jwt);
        store.dispatch(config.setAuthenticatedTrue(userId));

        // Always run user sync (needed for header/avatar)
        authCheckForUpdates(config, store);

        // insert new code here
        const productName = config.getProductName();
        // Skip admin sync if in SuperAdmin view (not CloudAdmin '/admin' route)
        const isInSuperAdminView =
          config.isSuperAdmin &&
          config.isSuperAdmin() &&
          (!window.location || window.location.pathname !== '/admin');
        if (productName === 'Cloud' && jwtStillHere) {
          const userAdminState = store.getState().userAdmin;
          const { adminCostCenterList } = userAdminState;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- optional cost center id
          let forceUpdate: any;
          if (!userAdminState || !adminCostCenterList || adminCostCenterList.length < 1) {
            const initializeAdminSync = true;
            if (!isInSuperAdminView) {
              authUserAdminCheckForUpdates(config, store, initializeAdminSync, forceUpdate);
            }
          } else if (adminCostCenterList && Object.keys(adminCostCenterList).length > 0) {
            const currentExist = store.getState().userAdmin.costCenter;
            const { selectedProductTab } = store.getState().productTab;
            // if there is not an existing cost center, or the cost center does not contain a product access with the current selected product tab
            if (!currentExist) {
              // @TODO along with refactor of overall watcher, need to do some sort of smart
              // ordering interpretation of the products set the default selected product tab
              const costCenterObj =
                defaultCostCenterObject(adminCostCenterList, selectedProductTab) ||
                adminCostCenterList[Object.keys(adminCostCenterList)[0]];
              if (costCenterObj) {
                const action = CloudActions.userAdminCurrentCostCenter(costCenterObj);
                store.dispatch(action);
              }
            }

            const initializeAdminSync = false;
            // forceUpdate = currentExist.id;  FJ-CLOUD-377 don't force update if we're in polling
            // sync and have a current cost center

            if (!isInSuperAdminView) {
              authUserAdminCheckForUpdates(config, store, initializeAdminSync, forceUpdate);
            }
          }
        }
        // end insert new code;
        let pollInterval: number;
        const pollingAdjustment = config.userSyncPollCycle();

        let timeDiffSinceLastActive: number | null;

        function stepdownPolling(): boolean | null {
          if (config.getUserLastActiveTimestamp() > 1) {
            timeDiffSinceLastActive =
              CloudUtils.getCurrentEpochTimeUtc() - config.getUserLastActiveTimestamp();
          } else {
            timeDiffSinceLastActive = null;
          }
          if (timeDiffSinceLastActive && timeDiffSinceLastActive > 15 * 60 * 1000) {
            return true;
          }
          return null;
        }
        if (stepdownPolling()) {
          pollInterval = 60 * 1000;
          setTimeout(cycleAuthCheckForUpdates, pollInterval);
        } else {
          if (pollingAdjustment) {
            pollInterval = pollingAdjustment;
          } else {
            pollInterval = 5 * 1000;
          }

          setTimeout(cycleAuthCheckForUpdates, pollInterval);
        }
      } else {
        setTimeout(cycleAuthCheckForUpdates, 5000);
      }
    } else {
      config.launchAuth('Invalid Request Token');
    }
    return undefined;
  })();
}

export function unauthorizedUserAdminAccessAlert(store: Store): void {
  const errorType = CloudErrors.userAdminErrorTypes.CANNOT_AUTHORIZE_USER_ADMIN;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function handleEmailVerificationTokenSend(
  config: Config,
  store: Store,
  action: ReduxAction,
): Promise<unknown> {
  store.dispatch(CloudActions.emailVerificationFetching());
  const url = UserServiceUrls.emailVerificationUrl(config.serverUrl);
  return (Net.sendJson(url, action) as Promise<NetResponse>)
    .then(({ code }) => {
      if (code !== Net.reponses.OK) {
        // `emailVerificationFailed` takes no args and ignores any passed at runtime (it returns a
        // fixed `{ type }` action), so dropping the former args preserves runtime behavior exactly.
        store.dispatch(CloudActions.emailVerificationFailed());
      } else {
        store.dispatch(
          CloudActions.emailVerificationUnverified(action.userId, action.emailAddress),
        );
      }
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
}

export function handleEmailVerificationTokenResend(
  config: Config,
  store: Store,
  action: ReduxAction,
): Promise<unknown> {
  const url = UserServiceUrls.emailVerificationUrl(config.serverUrl);
  return (Net.sendJson(url, action) as Promise<NetResponse>)
    .then(({ code }) => {
      if (code !== Net.reponses.OK) {
        // `emailVerificationFailed` ignores any args at runtime; dropping them preserves behavior.
        store.dispatch(CloudActions.emailVerificationFailed());
        return;
      }
      store.dispatch(CloudActions.emailVerificationUnverified(action.userId, action.emailAddress));
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
}

export function handleEmailVerificationTokenVerify(
  config: Config,
  store: Store,
  action: ReduxAction,
): Promise<unknown> {
  store.dispatch(CloudActions.emailVerificationFetching());
  const url = UserServiceUrls.emailVerificationUrl(config.serverUrl);
  return (Net.sendJson(url, action) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        const formattedResult = JSON.parse(data);
        const { error } = formattedResult;
        if (
          error === 'Could not verify email address due to INVALID_TOKEN' ||
          error === 'Could not verify email address due to Error: Invalid user id'
        ) {
          // `emailVerificationFailed` ignores any args at runtime; dropping them preserves behavior.
          store.dispatch(CloudActions.emailVerificationFailed());
        } else if (error === 'Could not verify email address due to EXPIRED_TOKEN') {
          const action1 = CloudActions.emailVerificationTokenResend(
            action.userId,
            action.emailAddress,
          );
          store.dispatch(action1);
          const action2 = CloudActions.emailVerificationExpired(action.userId, action.emailAddress);
          store.dispatch(action2);
        } else if (error === 'Could not verify email address due to EMAIL_ALREADY_VERIFIED') {
          store.dispatch(CloudActions.emailVerificationVerified());
        }
        return;
      }
      const formattedResult = JSON.parse(data);
      const { error } = formattedResult;
      if (error === 'Could not verify email address due to INVALID_TOKEN') {
        const action1 = CloudActions.emailVerificationTokenResend(
          action.userId,
          action.emailAddress,
        );
        store.dispatch(action1);
      } else if (error === 'Could not verify email address due to EXPIRED_TOKEN') {
        const action1 = CloudActions.emailVerificationTokenResend(
          action.userId,
          action.emailAddress,
        );
        store.dispatch(action1);
      } else {
        store.dispatch(CloudActions.emailVerificationVerified());
      }
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
}

export function vanillaPromise(store: Store) {
  return (next: Next) =>
    (action: ReduxAction) => {
      if (typeof action.then !== 'function') {
        return next(action);
      }

      return Promise.resolve(action).then(store.dispatch);
    };
}

// TODO see if code below is still going to be used

export function authApplyUpdates(
  store: Store,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw server results (JSON string)
  serverResults: any,
): void {
  if (!store) {
    throw new Error('Unable to apply server updates.  Cannot access local store.');
  }
  let serverResultsJson;
  try {
    serverResultsJson = JSON.parse(serverResults).result;
  } catch (err) {
    throw new Error(
      `Unable to parse server update results: ${(err as Error).message} ${serverResults}`,
    );
  }

  let numUpdates = 0;

  // Loop through all updates from server

  // `createUserFromJson` exists on cloud-core's runtime `User` module but is absent from its
  // published types (user.d.ts only re-exports constants); bridge the type/runtime mismatch.
  const UserModule = User as unknown as { createUserFromJson: (json: unknown) => unknown };
  for (const userId in serverResultsJson) {
    let user = serverResultsJson[userId];
    user = UserModule.createUserFromJson(user);
    const action = CloudActions.setUser(user);
    store.dispatch(action);
    numUpdates += 1;
  }

  if (numUpdates > 0) {
    console.log(`Applied ${numUpdates} updated user(s) changes`);
  }
}

export function authGetUserFromStore(
  store: Store,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- user id boundary
  userId: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caller-supplied fallback value
  missing: any = null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- user object from untyped store
): any {
  if (!store) {
    throw new Error('Invalid store');
  }
  const users = store.getState().users || {};
  return users[userId] || missing;
}

// /**
//  * Returns the url to update a taskspace on a taskspace api server based on a
//  * serverSettings JSON config object
//  */
export function userUrl(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped server settings object
  serverSettings: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- user id boundary
  userId: any,
): string {
  return UserServiceUrls.userUrl(serverSettings.url, userId);
}

export function authFetchUserAsync(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped server settings object
  serverSettings: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- user id boundary
  userId: any,
): Promise<unknown> {
  const url = userUrl(serverSettings, userId);
  console.log(`Fetching user ${userId} @ ${url}`);
  return CloudUtils.getJson(url);
}

// /**
//  * Adds a user to the remote server
//  */
function addRemoteUser(serverSettings: Config, action: ReduxAction): Promise<string> {
  const { user } = action;
  const url = UserServiceUrls.userBaseUrl(serverSettings);

  console.log(`Adding remote user ${user.id} @ ${url}`);
  // var httpRequest = new HttpRequest();
  // httpRequest.open('PUT', url, false);
  // httpRequest.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
  const json = JSON.stringify(user);

  // httpRequest.send(json);

  return Promise.resolve(json);
  // TODO actually talk to server
  // return CloudUtils.sendJson(url, json)
  //   .then( (addResult) => {
  //     if (!addResult) {
  //       throw new Error('Unknown server response for user ' + user.id + ' add.');
  //     }
  //
  //     addResult = JSON.parse(addResult);
  //     if (!addResult.succeeded) {
  //       throw new Error(JSON.stringify(addResult.error));
  //     } else {
  //       return addResult.result;
  //     }
  //   });
}

export function cannotCreateUserAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_CREATE_USER;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissCreateUserAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_CREATE_USER;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleCreateNewUser(
  config: Config,
  store: Store,
  createUserAction: ReduxAction,
): Promise<unknown> {
  const url = UserServiceUrls.createUserUrl(config.serverUrl);
  return (Net.sendJson(url, createUserAction) as Promise<NetResponse>)

    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        cannotCreateUserAlert(store);
        return null;
      }
      dismissCreateUserAlert(store);
      const formattedData = JSON.parse(data);
      if (formattedData.succeeded) {
        const userId = formattedData.result.id;
        const email = createUserAction.primaryEmail;
        const sendEmailAction = CloudActions.emailVerificationTokenSend(userId, email);
        store.dispatch(sendEmailAction);
        const urlBeginning = `/?${CloudActions.EMAIL_VERIFICATION_UNVERIFIED}`;
        const urlParameterUserId = `&userId=${userId}`;
        const urlParameterEmailAddress = `&emailAddress=${email}`;
        const urlParameters = urlBeginning + urlParameterUserId + urlParameterEmailAddress;
        config.navigateTo(urlParameters);
        return null;
      }
    })
    .catch((err) => {
      console.log('Could not create user', err);
      cannotCreateUserAlert(store);
      return null;
    });
}

export function handleConvertPhantomUserToUser(
  config: Config,
  store: Store,
  action: ReduxAction,
): Promise<unknown> {
  const { productName, ...phantomUserAction } = action;
  const url = UserServiceUrls.convertPhantomUserUrl(config.serverUrl);
  return (Net.sendJson(url, phantomUserAction) as Promise<NetResponse>).then(({ data, code }) => {
    if (code !== Net.reponses.OK) {
      cannotCreateUserAlert(store);
      return null;
    }

    dismissCreateUserAlert(store);
    const { succeeded, result } = JSON.parse(data);
    if (succeeded) {
      // check if the user invite is expired or revoked
      if (result.expired === CloudErrors.inviteUserErrorTypes.INVITE_EXPIRED) {
        config.navigateTo(
          `/invite?${CloudErrors.inviteUserErrorTypes.INVITE_EXPIRED}&productName=${productName}&adminEmail=${result.adminEmail}`,
        );
      } else if (result.cancelled === CloudErrors.inviteUserErrorTypes.INVITE_CANCELLED) {
        config.navigateTo(
          `/invite?${CloudErrors.inviteUserErrorTypes.INVITE_CANCELLED}&productName=${productName}&adminEmail=${result.adminEmail}`,
        );
      } else if (!result.jwt && result.user) {
        // This is the case if the user's email was unable to be verified due to expired token.
        const userId = result.user.id;
        const email = result.user.primaryEmail;
        const sendEmailAction = CloudActions.emailVerificationTokenResend(userId, email);
        store.dispatch(sendEmailAction);
        config.navigateTo(
          `/?${CloudActions.EMAIL_VERIFICATION_UNVERIFIED}&userId=${userId}&emailAddress=${email}`,
        );
      } else if (!result) {
        console.log('why no result?');
      } else {
        // otherwise, sign in the user
        config.setAuthJwtToken(config.getAuthTokenKey(), result.jwt);
        config.navigateTo(`/?${CloudActions.CREATE_NEW_USER_SUCCESS}&productName=${productName}`);
      }
      return null;
    }
    cannotCreateUserAlert(store);
    // ES2020 lib types `Error` as single-arg; the original code passes a second (diagnostic)
    // argument that is ignored at runtime. Widen the constructor type to preserve behavior.
    const ErrorCtor = Error as unknown as new (message?: string, options?: unknown) => Error;
    return Promise.reject(
      new ErrorCtor('Could not convert phantom user to user', { succeeded, result }),
    );
  });
}

const REGISTER_PRODUCT_TRIAL_ALERT_TYPES = [
  CloudErrors.cloudErrorTypes.CANNOT_REGISTER_SEQ_GEQ_TRIAL,
  CloudErrors.cloudErrorTypes.CANNOT_REGISTER_PRODUCT_TRIAL_REGION_RESTRICTED,
];

export function cannotRegisterProductTrialAlert(
  store: Store,
  errorType: string = CloudErrors.cloudErrorTypes.CANNOT_REGISTER_SEQ_GEQ_TRIAL,
): void {
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissRegisterProductTrialAlert(store: Store): void {
  REGISTER_PRODUCT_TRIAL_ALERT_TYPES.forEach((errorType) => {
    store.dispatch(Alerts.AlertActions.dismissAlert(errorType));
  });
}

export function handleRegisterProductTrial(
  config: Config,
  store: Store,
  registerProductTrialAction: ReduxAction,
): Promise<unknown> {
  const { productName } = registerProductTrialAction;
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const url = ProductServiceUrls.trialUrl(config.serverUrl);
  const headers = { Authorization: `Bearer ${authToken}` };
  return (Net.sendJson(url, registerProductTrialAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      // `data` is the raw response text, not a parsed object - see the NetResponse comment above.
      const parsedData = JSON.parse(data);
      if (code !== Net.reponses.OK) {
        const userMessageCode = parsedData?.errors?.[0]?.userMessageCode;
        const errorType =
          userMessageCode === CloudErrors.licenseMessageCodes.TRIAL_REGION_RESTRICTED
            ? CloudErrors.cloudErrorTypes.CANNOT_REGISTER_PRODUCT_TRIAL_REGION_RESTRICTED
            : CloudErrors.cloudErrorTypes.CANNOT_REGISTER_SEQ_GEQ_TRIAL;
        cannotRegisterProductTrialAlert(store, errorType);
        return null;
      }
      const costCenter = parsedData.result;
      dismissRegisterProductTrialAlert(store);
      config.navigateTo(`/?productName=${productName}&trialSuccess`);
      return costCenter;
    })
    .catch((err) => {
      console.log('Could not register product trial', err);
      cannotRegisterProductTrialAlert(store);
      return null;
    });
}

export function successGeneratePasswordResetCodeAlert(store: Store): void {
  const errorType = CloudErrors.forgotPasswordErrorTypes.SUCCESS_GENERATE_PASSWORD_RESET_CODE;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function cannotGeneratePasswordResetCodeAlert(store: Store): void {
  const errorType = CloudErrors.forgotPasswordErrorTypes.NO_USER_PASSWORD_RESET_CODE;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissCannotGeneratePasswordResetCodeAlert(store: Store): void {
  // `NO_USER_PASSWORD_RESET_CODE` is not a member of `cloudErrorTypes` (it lives on
  // `forgotPasswordErrorTypes`), so at runtime this access resolves to `undefined`. Cast through an
  // index signature to preserve that exact pre-existing runtime value.
  const errorType = (CloudErrors.cloudErrorTypes as Record<string, string>)
    .NO_USER_PASSWORD_RESET_CODE;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleGeneratePasswordResetCode(
  config: Config,
  store: Store,
  generatePasswordResetCodeAction: ReduxAction,
): Promise<unknown> {
  const url = AuthServiceUrls.generateResetCodeUrl(config.serverUrl);
  return (Net.sendJson(url, generatePasswordResetCodeAction) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK || data.succeeded === false) {
        cannotGeneratePasswordResetCodeAlert(store);
        return null;
      }
      const { result } = data;
      dismissCannotGeneratePasswordResetCodeAlert(store);
      successGeneratePasswordResetCodeAlert(store);
      return result;
    })
    .catch((err) => {
      console.log('Could not generate password reset code', err);
      cannotGeneratePasswordResetCodeAlert(store);
      return null;
    });
}

export function successPasswordReset(store: Store): void {
  const errorType = CloudErrors.forgotPasswordErrorTypes.SUCCESS_PASSWORD_RESET;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function invalidPasswordResetCodeAlert(store: Store): void {
  const errorType = CloudErrors.forgotPasswordErrorTypes.INVALID_PASSWORD_RESET_CODE;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissInvalidPasswordResetCodeAlert(store: Store): void {
  const errorType = CloudErrors.forgotPasswordErrorTypes.INVALID_PASSWORD_RESET_CODE;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleResetForgottenPassword(
  config: Config,
  store: Store,
  resetForgottenPasswordAction: ReduxAction,
): Promise<unknown> {
  const url = AuthServiceUrls.resetForgottenPasswordUrl(config.serverUrl);
  return (Net.sendJson(url, resetForgottenPasswordAction) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        if (data) {
          data = JSON.parse(data);
        }
        if (
          data.error === CloudErrors.forgotPasswordErrorTypes.INVALID_PASSWORD_RESET_CODE ||
          data.error === CloudErrors.forgotPasswordErrorTypes.EXPIRED_PASSWORD_RESET_CODE ||
          data.error === CloudErrors.forgotPasswordErrorTypes.NO_USER_PASSWORD_RESET_CODE
        ) {
          invalidPasswordResetCodeAlert(store);
        }
        return;
      }
      dismissInvalidPasswordResetCodeAlert(store);
      successPasswordReset(store);
    })
    .catch((err) => {
      console.log('Could not reset forgotten password', err);
      invalidPasswordResetCodeAlert(store);
      return null;
    });
}

export function cannotChangePasswordAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissChangePasswordAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function successChangePasswordAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissSuccessChangePasswordAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleChangePassword(
  config: Config,
  store: Store,
  changePasswordAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const url = AuthServiceUrls.changePasswordUrl(config.serverUrl);
  const headers = { Authorization: `Bearer ${authToken}` };
  return (Net.sendJson(url, changePasswordAction, headers) as Promise<NetResponse>)
    .then(({ code }) => {
      if (code !== Net.reponses.OK) {
        cannotChangePasswordAlert(store);
        return;
      }
      dismissChangePasswordAlert(store);
      successChangePasswordAlert(store);
    })
    .catch((err) => {
      console.log('Couldnt change password', err);
      cannotChangePasswordAlert(store);
      return null;
    });
}

export function cannotCreateCostCenterAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_CREATE_COST_CENTER;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissCreateCostCenterAlert(store: Store): void {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_CREATE_COST_CENTER;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleCreateNewCostCenter(
  config: Config,
  store: Store,
  createCostCenterAction: ReduxAction,
): Promise<unknown> {
  const url = CostCenterServiceUrls.createCostCenterUrl(config.serverUrl);
  return (Net.sendJson(url, createCostCenterAction) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        cannotCreateCostCenterAlert(store);
        return null;
      }
      const costCenter = data.result;
      dismissCreateCostCenterAlert(store);
      return costCenter;
    })
    .catch((err) => {
      console.log('Could not create costCenter', err);
      cannotCreateCostCenterAlert(store);
      return null;
    });
}

export function handleProductAddUsers(
  config: Config,
  store: Store,
  productAddUsersAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = ProductServiceUrls.productUrl(config.serverUrl);
  // `storeProductAddUsersResult` exists at runtime but is absent from cloud-core's published types.
  const storeProductAddUsersResult = (
    CloudActions as unknown as { storeProductAddUsersResult: (result: unknown) => unknown }
  ).storeProductAddUsersResult;
  return (Net.sendJson(url, productAddUsersAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        const formattedResult = JSON.parse(data);
        const storeResultAction = storeProductAddUsersResult(formattedResult);
        store.dispatch(storeResultAction);
        return;
      }
      const formattedResult = JSON.parse(data);
      const storeResultAction = storeProductAddUsersResult(formattedResult);
      store.dispatch(storeResultAction);
      forceUpdateCurrentCostCenter(store);
    })
    .catch((err) => {
      console.log('Could not add user seat', err);
    });
}

export function cannotProductAddUsersAlert(store: Store): void {
  // `seqGeqErrorTypes` exists at runtime but is absent from cloud-core's published types.
  const errorType = (CloudErrors as unknown as { seqGeqErrorTypes: Record<string, string> })
    .seqGeqErrorTypes.CANNOT_ADD_USERS;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

// @TODO why was this taken out
export function dismissProductAddUsersAlert(): void {
  // const errorType = CloudErrors.seqGeqErrorTypes.CANNOT_ADD_USERS;
  // const action = Alerts.AlertActions.dismissAlert(errorType);
  // store.dispatch(action);
}

export function cannotSetUsersSuspendedAlert(store: Store): void {
  // `seqGeqErrorTypes` exists at runtime but is absent from cloud-core's published types.
  const errorType = (CloudErrors as unknown as { seqGeqErrorTypes: Record<string, string> })
    .seqGeqErrorTypes.CANNOT_SET_SUSPENDED;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissSetUsersSuspendedAlert(store: Store): void {
  const errorType = CloudErrors.productErrorTypes.CANNOT_SET_SUSPENDED;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleSetUsersSuspended(
  config: Config,
  store: Store,
  productSetSuspendedAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = ProductServiceUrls.productUrl(config.serverUrl);
  return (Net.sendJson(url, productSetSuspendedAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        const formattedResult = JSON.parse(data);
        const storeResultAction = CloudActions.storeSetUsersSuspendedResult(formattedResult);
        store.dispatch(storeResultAction);
        cannotSetUsersSuspendedAlert(store);
      } else {
        const formattedResult = JSON.parse(data);
        const storeResultAction = CloudActions.storeSetUsersSuspendedResult(formattedResult);
        store.dispatch(storeResultAction);
        dismissSetUsersSuspendedAlert(store);
        forceUpdateCurrentCostCenter(store);
      }
    })
    .catch((err) => {
      console.log('Could not set user seat to suspended', err);
      cannotSetUsersSuspendedAlert(store);
    });
}

export function cannotProductSetUsersEndDateAlert(store: Store): void {
  const errorType = CloudErrors.productErrorTypes.CANNOT_SET_END_DATE;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissProductSetUsersEndDateAlert(store: Store): void {
  const errorType = CloudErrors.productErrorTypes.CANNOT_SET_END_DATE;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleProductSetUsersEndDate(
  config: Config,
  store: Store,
  productSetEndDateAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = ProductServiceUrls.productUrl(config.serverUrl);
  return (Net.sendJson(url, productSetEndDateAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        cannotProductSetUsersEndDateAlert(store);
      } else {
        const formattedResult = JSON.parse(data);
        const storeResultAction = CloudActions.storeProductSetUsersEndDateResult(formattedResult);
        store.dispatch(storeResultAction);
        dismissProductSetUsersEndDateAlert(store);
        forceUpdateCurrentCostCenter(store);
      }
    })
    .catch((err) => {
      console.log('Could not set user seat end date', err);
      cannotProductSetUsersEndDateAlert(store);
    });
}

export function cannotProductSetUsersArchivedDateAlert(store: Store): void {
  // `CANNOT_SET_ARCHIVED_DATE` is absent from cloud-core's `productErrorTypes`, so at runtime this
  // access resolves to `undefined`. Cast through an index signature to preserve that exact
  // pre-existing runtime value.
  const errorType = (CloudErrors.productErrorTypes as Record<string, string>)
    .CANNOT_SET_ARCHIVED_DATE;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

export function dismissProductSetUsersArchivedDateAlert(store: Store): void {
  // `CANNOT_SET_ARCHIVED_DATE` is absent from cloud-core's `productErrorTypes`, so at runtime this
  // access resolves to `undefined`. Cast through an index signature to preserve that exact
  // pre-existing runtime value.
  const errorType = (CloudErrors.productErrorTypes as Record<string, string>)
    .CANNOT_SET_ARCHIVED_DATE;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

export function handleProductSetUsersArchivedDate(
  config: Config,
  store: Store,
  productSetArchivedDateAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = ProductServiceUrls.productUrl(config.serverUrl);
  return (Net.sendJson(url, productSetArchivedDateAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK) {
        cannotProductSetUsersArchivedDateAlert(store);
      } else {
        const formattedResult = JSON.parse(data);
        const storeResultAction =
          CloudActions.storeProductSetUsersArchivedDateResult(formattedResult);
        store.dispatch(storeResultAction);
        dismissProductSetUsersArchivedDateAlert(store);
        forceUpdateCurrentCostCenter(store);
      }
    })
    .catch((err) => {
      console.log('Could not set user seat archived date', err);
      cannotProductSetUsersArchivedDateAlert(store);
    });
}

export function handleGetUserAgreementAcceptance(
  config: Config,
  store: Store,
  getUserAgreementAcceptanceAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = LicenseServiceUrls.userAgreementAcceptedUrl(config.serverUrl);
  return (Net.sendJson(url, getUserAgreementAcceptanceAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK || data.succeeded === false) {
        return null;
      }
      const { result } = data;
      console.log('Retrieved user agreement acceptance', data);
      return result;
    })
    .catch((err) => {
      console.log('Could not get user agreement acceptance', err);
      return null;
    });
}

export function handleSetUserAgreementAcceptance(
  config: Config,
  store: Store,
  setUserAgreementAcceptanceAction: ReduxAction,
): Promise<unknown> {
  const authToken = config.getAuthJwtToken(config.getAuthTokenKey());
  const headers = { Authorization: `Bearer ${authToken}` };
  const url = LicenseServiceUrls.userAgreementAcceptedUrl(config.serverUrl);
  return (Net.sendJson(url, setUserAgreementAcceptanceAction, headers) as Promise<NetResponse>)
    .then(({ data, code }) => {
      if (code !== Net.reponses.OK || data.succeeded === false) {
        return null;
      }
      dismissSignInConnectionProblem(store);
      const { result } = data;
      console.log('Set user agreement acceptance', data);
      const action = CloudActions.userHasAgreed();
      store.dispatch(action);
      return result;
    })
    .catch((err) => {
      console.log('Could not set user agreement acceptance', err);
      return null;
    });
}

/*
 * Redux middleware for Cloud actions.
 */

export function AuthClientMiddleware(config: Config) {
  let stopWatcher: (() => void) | undefined;

  function authMiddleware(store: Store) {
    return (next: Next) => (action: ReduxAction) => {
      let result;

      switch (action.type) {
        case CloudActions.START_CLOUD_WATCHER:
          cloudWatcher(config, store);
          break;
        case CloudActions.EMAIL_VERIFICATION_TOKEN_SEND:
          result = handleEmailVerificationTokenSend(config, store, action);
          break;
        case CloudActions.EMAIL_VERIFICATION_TOKEN_RESEND:
          result = handleEmailVerificationTokenResend(config, store, action);
          break;
        case CloudActions.EMAIL_VERIFICATION_TOKEN_VERIFY:
          result = handleEmailVerificationTokenVerify(config, store, action);
          break;
        case CloudActions.ADD_USER:
          result = addRemoteUser(config, action);
          break;
        case CloudActions.SUBSCRIBE_TO_SITE:
          if (stopWatcher) {
            stopWatcher();
          }
          // TODO clear cloud action on each subscribe
          // stopWatcher = cloudWatcher(getCookie, serverUrl, store);
          return next(action);
        case CloudActions.UNSUBSCRIBE_TO_SITE:
          if (stopWatcher) {
            stopWatcher();
          }
          return next(action);
        // Cannot import type from auth-client because it would break the one-directional dependency
        // chain. However, @TODO incrementally refactor by adding an action callback to the config
        // object.
        case 'SIGN_OUT_SAGA':
          if (stopWatcher) {
            stopWatcher();
          }
          result = next(action);
          return result;
        case CloudActions.CREATE_NEW_USER:
          result = handleCreateNewUser(config, store, action);
          break;
        case CloudActions.CONVERT_PHANTOM_USER_TO_USER:
          result = handleConvertPhantomUserToUser(config, store, action);
          break;
        case CloudActions.REGISTER_PRODUCT_TRIAL:
          result = handleRegisterProductTrial(config, store, action);
          break;
        case CloudActions.GENERATE_PASSWORD_RESET_CODE:
          result = handleGeneratePasswordResetCode(config, store, action);
          break;
        case CloudActions.RESET_FORGOTTEN_PASSWORD:
          result = handleResetForgottenPassword(config, store, action);
          break;
        case CloudActions.CHANGE_PASSWORD:
          result = handleChangePassword(config, store, action);
          break;
        case CloudActions.CREATE_NEW_COST_CENTER:
          result = handleCreateNewCostCenter(config, store, action);
          break;
        // TODO investigate, because all user agreement related interceptions should be in
        // agreement-client saga
        case CloudActions.USER_AGREEMENT_ACCEPTED_GET:
          result = handleGetUserAgreementAcceptance(config, store, action);
          break;
        case CloudActions.USER_AGREEMENT_ACCEPTED_SET:
          result = handleSetUserAgreementAcceptance(config, store, action);
          break;
        case CloudActions.PRODUCT_ADD_USERS:
          result = handleProductAddUsers(config, store, action);
          break;
        case CloudActions.PRODUCT_SET_USERS_SUSPENDED:
          result = handleSetUsersSuspended(config, store, action);
          break;
        case CloudActions.PRODUCT_SET_USERS_END_DATE:
          result = handleProductSetUsersEndDate(config, store, action);
          break;
        case CloudActions.PRODUCT_SET_USERS_ARCHIVED_DATE:
          result = handleProductSetUsersArchivedDate(config, store, action);
          break;
        case CloudActions.USER_ADMIN_FORCE_UPDATE: {
          const initializeAdminSync = false;
          const forceUpdate = action.costCenterId;
          authUserAdminCheckForUpdates(config, store, initializeAdminSync, forceUpdate);
          break;
        }
        default:
          return next(action);
      }
      next(action);
      return undefined;
    };
  }

  return authMiddleware;
}
